import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

const API_URL = import.meta.env.VITE_API_URL;

export type CustomerCarrierState =
  | "active"
  | "not_added"
  | "removed"
  | "blocked";

export interface CustomerCarrierConnection {
  id: string;
  status: "ACTIVE" | "BLOCKED";
  customerRemovedAt: string | null;
  tierId: string | null;
  customDiscountPercentage: number | null;
}

export interface CustomerCarrier {
  id: string;
  code: string;
  name: string;
  logoUrl?: string | null;
  customerCanToggle: boolean;
  state: CustomerCarrierState;
  connection: CustomerCarrierConnection | null;
}

export interface CustomerCarriersResponse {
  data: CustomerCarrier[];
}

export const customerCarrierQueryKey = (customerId?: string) =>
  ["customer-carriers", customerId] as const;

function applyState(
  previous: CustomerCarriersResponse | undefined,
  carrierId: string,
  state: CustomerCarrierState
): CustomerCarriersResponse | undefined {
  if (!previous) return previous;

  return {
    data: previous.data.map((carrier) =>
      carrier.id === carrierId
        ? {
            ...carrier,
            state,
            connection: carrier.connection
              ? {
                  ...carrier.connection,
                  status: state === "blocked" ? "BLOCKED" : "ACTIVE",
                  customerRemovedAt:
                    state === "removed"
                      ? new Date().toISOString()
                      : state === "active"
                        ? null
                        : carrier.connection.customerRemovedAt,
                }
              : carrier.connection,
          }
        : carrier
    ),
  };
}

export function useCustomerCarriers() {
  const { customer, token } = useSession();

  return useQuery<CustomerCarriersResponse>({
    queryKey: customerCarrierQueryKey(customer?.id),
    queryFn: async () => {
      if (!token) throw new Error("Not authenticated");
      const { data } = await axios.get<CustomerCarriersResponse>(
        `${API_URL}/api/v2/customer/carriers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!customer?.id && !!token,
  });
}

export function useAddCustomerCarrier() {
  const { customer, token } = useSession();
  const queryClient = useQueryClient();
  const queryKey = customerCarrierQueryKey(customer?.id);

  return useMutation({
    mutationFn: async (carrierId: string) => {
      if (!token) throw new Error("Not authenticated");
      const { data } = await axios.post(
        `${API_URL}/api/v2/customer/carriers/${carrierId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onMutate: async (carrierId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<CustomerCarriersResponse>(queryKey);
      queryClient.setQueryData(
        queryKey,
        applyState(previous, carrierId, "active")
      );
      return { previous };
    },
    onError: (_error, _carrierId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRemoveCustomerCarrier() {
  const { customer, token } = useSession();
  const queryClient = useQueryClient();
  const queryKey = customerCarrierQueryKey(customer?.id);

  return useMutation({
    mutationFn: async (carrierId: string) => {
      if (!token) throw new Error("Not authenticated");
      const { data } = await axios.delete(
        `${API_URL}/api/v2/customer/carriers/${carrierId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onMutate: async (carrierId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<CustomerCarriersResponse>(queryKey);
      queryClient.setQueryData(
        queryKey,
        applyState(previous, carrierId, "removed")
      );
      return { previous };
    },
    onError: (_error, _carrierId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
